import React, { useCallback, useEffect } from "react";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { PagePaper } from "@/components/Paper";
import {
  type ChibanInput,
  type ChibanInputRow,
  type SearchResultRow,
  type KaokuNumberToChibanSearchParam,
  type KaokuNumberInput,
} from "@/types/kaokuNumber";
import { Button, ButtonVariantOption } from "@/components/Button";

import CloseIcon from "@mui/icons-material/Close";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useKaokuNumber } from "@/hooks/useKaokuNumber";
import { ViewAreaSearchInput } from "@/components/KaokuNumberSearch/ViewAreaSearchInput";
import { ViewAreaSearchResult } from "@/components/KaokuNumberSearch/ViewAreaSearchResult";
import { ConfirmDialogSearch } from "@/components/KaokuNumberSearch/ConfirmDialogSearch";
import { ConfirmDialogKaokuNumber } from "@/components/KaokuNumberSearch/ConfirmDialogKaokuNumber";
import { EmptyStateMessage } from "@/components/KaokuNumberSearch/EmptyStateMessage";
import { HttpStatusCode, isAxiosError } from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";
import { toast } from "react-toastify";
import { type PropertySelectionRow } from "@/features/map/types";
import { useChiban } from "@/hooks/useChiban";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useIsMobile } from "@/hooks/useIsMobile";

type Variant = "map" | "book";

interface Props {
  variant: Variant;
  chibanList: ChibanInput[];
  setKaokuNumberSearchModalIsOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  appendPropertySelectionRows: (rows: PropertySelectionRow[]) => void;
  handleClickSubmitLandSearchButtonRef?: React.MutableRefObject<
    ((cancelLoading: () => void) => void) | undefined
  >;
  hideChibanList?: boolean;
  hideSearchTab?: boolean;
  hideListButton?: boolean;
  hideCloseButton?: boolean;
  hideTitle?: boolean;
  onKaokuNumberDataChange?: (
    kaokuNumberRows: SearchResultRow[],
    kaokuNumberSelectedIds: GridSelectionModel
  ) => void;
  initialKaokuNumberRows?: SearchResultRow[];
  initialSelectedIds?: GridSelectionModel;
}

const MobileContentWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <Box sx={{ pt: 2 }}>{children}</Box>;

/**
 * 不動産登記/図面取得（個別）の家屋番号検索モーダル
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const ChibanKaokuNumberSearchModal: React.FC<Props> = (props) => {
  const {
    variant,
    chibanList,
    setKaokuNumberSearchModalIsOpen,
    appendPropertySelectionRows,
    handleClickSubmitLandSearchButtonRef,
    hideChibanList = false,
    hideSearchTab = false,
    hideListButton = false,
    hideCloseButton = false,
    hideTitle = false,
    onKaokuNumberDataChange,
    initialKaokuNumberRows,
    initialSelectedIds,
  } = props;

  const isMobile = useIsMobile();

  // データを土地と建物に分離
  const landData = chibanList.filter((item) => item.bookType === "LAND");
  const buildingData = chibanList.filter(
    (item) =>
      item.bookType === "BUILDING" ||
      item.bookType === "CONDOMINIUM" ||
      item.bookType === "ONE_BUILDING"
  );

  // タブ状態管理 - データがある方から開始
  const getInitialTab = (): number => {
    if (landData.length > 0 && buildingData.length === 0) return 0; // 土地のみ
    if (landData.length === 0 && buildingData.length > 0) return 1; // 建物のみ
    return 0; // 両方あるか両方ない場合は土地タブ
  };
  const [activeTab, setActiveTab] = React.useState<number>(getInitialTab());

  // 土地の場合は地番→家屋番号検索
  const landChibanList: ChibanInput[] = landData;

  // 建物の場合は家屋番号→地番検索用にchibanをkaokuNumberとして設定
  const buildingKaokuList: KaokuNumberInput[] = buildingData.map((item) => ({
    prefectureName: item.prefectureName,
    location: item.location,
    kaokuNumber: item.chiban, // chibanを家屋番号として扱う
    bookType: item.bookType,
  }));
  // 土地用のフック（地番→家屋番号）
  const {
    convertResultToKaokuNumbers: landConvertResultToKaokuNumbers,
    chibanInputRows: landChibanInputRows,
    setChibanInputRows: landSetChibanInputRows,
    setHitCountsFromResponse: landSetHitCountsFromResponse,
    convertToPropertySelectionRows: landConvertToPropertySelectionRows,
    createKaokuNumberSearchRequest: landCreateKaokuNumberSearchRequest,
    getKaokuNumberMaxRequestCount: landGetKaokuNumberMaxRequestCount,
    getKaokuNumberSearchRequest: landGetKaokuNumberSearchRequest,
    kaokuNumberRows: landKaokuNumberRows,
    setKaokuNumberRows: landSetKaokuNumberRows,
    loading: landLoading,
    setLoading: landSetLoading,
    selectedChibanRowIds: landSelectedChibanRowIds,
    setSelectedChibanRowIds: landSetSelectedChibanRowIds,
    selectedKaokuNumberRowIds: landSelectedKaokuNumberRowIds,
    setSelectedKaokuNumberRowIds: landSetSelectedKaokuNumberRowIds,
    openSearchModalFlag: landOpenSearchModalFlag,
    setOpenSearchModalFlag: landSetOpenSearchModalFlag,
    openAddRowsModalFlag: landOpenAddRowsModalFlag,
    setOpenAddRowsModalFlag: landSetOpenAddRowsModalFlag,
    pollingRequestId: landPollingRequestId,
    setPollingRequestId: landSetPollingRequestId,
  } = useKaokuNumber(landChibanList);

  // 建物用のフック（家屋番号→地番）
  const {
    convertResultToChibans: buildingConvertResultToChibans,
    convertToPropertySelectionRows: buildingConvertToPropertySelectionRows,
    kaokuNumberRows: buildingKaokuNumberRows,
    setKaokuNumberRows: buildingSetKaokuNumberRows,
    setHitCountsFromResponse: buildingSetHitCountsFromResponse,
    chibanRows: buildingChibanRows,
    setChibanRows: buildingSetChibanRows,
    loading: buildingLoading,
    setLoading: buildingSetLoading,
    selectedKaokuNumberRowIds: buildingSelectedKaokuNumberRowIds,
    setSelectedKaokuNumberRowIds: buildingSetSelectedKaokuNumberRowIds,
    selectedChibanRowIds: buildingSelectedChibanRowIds,
    setSelectedChibanRowIds: buildingSetSelectedChibanRowIds,
    openAddRowsModalFlag: buildingOpenAddRowsModalFlag,
    setOpenAddRowsModalFlag: buildingSetOpenAddRowsModalFlag,
    openSearchModalFlag: buildingOpenSearchModalFlag,
    setOpenSearchModalFlag: buildingSetOpenSearchModalFlag,
    getChibanMaxRequestCount: buildingGetChibanMaxRequestCount,
    createChibanSearchRequest: buildingCreateChibanSearchRequest,
    getChibanSearchRequest: buildingGetChibanSearchRequest,
    pollingRequestId: buildingPollingRequestId,
    setPollingRequestId: buildingSetPollingRequestId,
  } = useChiban(buildingKaokuList);

  const landMaxRequestCount = landGetKaokuNumberMaxRequestCount();
  const buildingMaxRequestCount = buildingGetChibanMaxRequestCount();

  // タブ切り替えハンドラ
  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setActiveTab(newValue);
  };

  // 家屋番号→地番 検索用（useChibanフックに統合）

  const handleChibanRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      landSetSelectedChibanRowIds(newSelectionModel);
    },
    [landSetSelectedChibanRowIds]
  );

  const handleKaokuNumberRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      landSetSelectedKaokuNumberRowIds(newSelectionModel);
    },
    [landSetSelectedKaokuNumberRowIds]
  );

  const handleBuildingKaokuNumberRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      buildingSetSelectedKaokuNumberRowIds(newSelectionModel);
    },
    [buildingSetSelectedKaokuNumberRowIds]
  );

  const handleBuildingChibanRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      buildingSetSelectedChibanRowIds(newSelectionModel);
    },
    [buildingSetSelectedChibanRowIds]
  );

  useEffect(() => {
    if (!landPollingRequestId) {
      return;
    }

    // 3秒ごとにポーリング
    const intervalId = setInterval(() => {
      void fetchLandKaokuNumberSearchRequest();
    }, 3000);

    // 15分でタイムアウト
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      landSetPollingRequestId(null);
    }, 1000 * 60 * 15);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [landPollingRequestId]);

  useEffect(() => {
    if (!buildingPollingRequestId) return;
    const intervalId = setInterval(() => {
      void fetchBuildingChibanSearchRequest();
    }, 3000);
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      buildingSetPollingRequestId(null);
    }, 1000 * 60 * 15);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [buildingPollingRequestId]);

  // 初期値が渡されている場合、検索結果を復元する
  useEffect(() => {
    if (initialKaokuNumberRows && initialKaokuNumberRows.length > 0) {
      landSetKaokuNumberRows(initialKaokuNumberRows);
      if (initialSelectedIds) {
        landSetSelectedKaokuNumberRowIds(initialSelectedIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKaokuNumberRows, initialSelectedIds]); // 初期値が変更された時（ステップ切り替え時など）に実行

  // 家屋番号検索の結果の選択項目が変化した時に親のコンポーネントに変更内容を伝える
  useEffect(() => {
    if (typeof onKaokuNumberDataChange === "function") {
      onKaokuNumberDataChange?.(
        landKaokuNumberRows,
        landSelectedKaokuNumberRowIds
      );
    }
  }, [landKaokuNumberRows, landSelectedKaokuNumberRowIds]);

  const handleClickSubmitLandSearchButton = (
    cancelLoading: () => void
  ): void => {
    landSetLoading(true);

    // 検索開始時にヒット件数をリセット
    landSetChibanInputRows((prevRows) =>
      prevRows.map((row) => ({ ...row, hitCount: undefined }))
    );

    // リクエスト作成APIを呼び出す
    const selectedChibanList = landChibanInputRows
      .filter((row: ChibanInputRow) => {
        return landSelectedChibanRowIds.includes(row.id);
      })
      .map((row: ChibanInputRow) => {
        return {
          prefectureName: row.prefectureName,
          location: row.location,
          chiban: row.chiban,
        };
      });

    (async () => {
      try {
        const response = await landCreateKaokuNumberSearchRequest(
          selectedChibanList
        );

        if (response) {
          if (response.status === HttpStatusCode.Accepted && response.id) {
            landSetPollingRequestId(response.id);
            landSetOpenSearchModalFlag(false);
            return;
          } else if (response.status === HttpStatusCode.BadRequest) {
            landSetLoading(false);
            if (
              response.error &&
              response.error === "登記情報提供サービスの営業時間外です"
            ) {
              toast.error("登記情報提供サービスの営業時間外です");
            } else {
              toast.error(
                `件数は${formatNumberWithComma(
                  landMaxRequestCount
                )}件以下にしてください。`
              );
            }
          }
        }
      } catch (error) {
        landSetLoading(false);
        if (
          isAxiosError(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }
      } finally {
        cancelLoading();
      }
    })();
  };

  // 外部コンポーネントから家屋番号検索を実行できるようにする
  // handleClickSubmitLandSearchButtonRef.current()で外部コンポーネントから呼び出すことができる
  if (handleClickSubmitLandSearchButtonRef) {
    handleClickSubmitLandSearchButtonRef.current =
      handleClickSubmitLandSearchButton;
  }

  const handleClickSubmitBuildingSearchButton = (
    cancelLoading: () => void
  ): void => {
    buildingSetLoading(true);

    // 検索開始時にヒット件数をリセット（前回の検索結果を残さない）
    buildingSetKaokuNumberRows((prevRows) =>
      prevRows.map((row) => ({ ...row, hitCount: undefined }))
    );

    const selected = buildingKaokuNumberRows.filter((r) =>
      buildingSelectedKaokuNumberRowIds.includes(r.id)
    );
    const payload: KaokuNumberToChibanSearchParam[] = selected.map((row) => ({
      prefectureName: row.prefectureName,
      location: row.location,
      kaokuNumber: row.kaokuNumber,
    }));

    (async () => {
      try {
        const res = await buildingCreateChibanSearchRequest(payload);
        if (res) {
          if (res.status === HttpStatusCode.Accepted && res.id) {
            buildingSetPollingRequestId(res.id);
            buildingSetOpenSearchModalFlag(false);
            return;
          } else if (res.status === HttpStatusCode.BadRequest) {
            buildingSetLoading(false);
            if (
              res.error &&
              res.error === "登記情報提供サービスの営業時間外です"
            ) {
              toast.error("登記情報提供サービスの営業時間外です");
            } else {
              toast.error(
                `件数は${formatNumberWithComma(
                  buildingMaxRequestCount
                )}件以下にしてください。`
              );
            }
          }
        }
      } catch (e) {
        buildingSetLoading(false);
        if (
          isAxiosError(e) &&
          (e.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(e);
        }
      } finally {
        cancelLoading();
      }
    })();
  };

  const fetchLandKaokuNumberSearchRequest = async (): Promise<void> => {
    if (!landPollingRequestId) {
      return;
    }

    const response = await landGetKaokuNumberSearchRequest(
      landPollingRequestId
    );
    if (response) {
      if (response.status === "COMPLETED") {
        const convertedKaokuNumbers = landConvertResultToKaokuNumbers(response);
        const rows = convertedKaokuNumbers;
        landSetKaokuNumberRows(rows);
        landSetSelectedKaokuNumberRowIds(
          rows
            .filter((row: SearchResultRow) => row.selected)
            .map((row: SearchResultRow) => row.id)
        );

        // 検索結果から地番ごとのヒット件数を集計して、入力行を更新
        landSetHitCountsFromResponse(response);

        landSetPollingRequestId(null);
        landSetLoading(false);

        if (rows.length === 0) {
          toast.info("検索結果は0件です");
        }
      }
    }
  };

  const fetchBuildingChibanSearchRequest = async (): Promise<void> => {
    if (!buildingPollingRequestId) return;
    const response = await buildingGetChibanSearchRequest(
      buildingPollingRequestId
    );
    if (response && response.status === "COMPLETED") {
      const convertedChibans = buildingConvertResultToChibans(response);
      const rows = convertedChibans;
      buildingSetChibanRows(rows);
      buildingSetSelectedChibanRowIds(rows.map((r) => r.id));

      // 検索結果から家屋番号ごとのヒット件数を集計して、入力行を更新
      buildingSetHitCountsFromResponse(response);

      buildingSetPollingRequestId(null);
      buildingSetLoading(false);
      if (rows.length === 0) {
        toast.info("検索結果は0件です");
      }
    }
  };

  const handleClickSubmitLandAddRowsButton = (
    cancelLoading: () => void
  ): void => {
    const landRows = landConvertToPropertySelectionRows();
    appendPropertySelectionRows(landRows);
    landSetOpenAddRowsModalFlag(false);
    setKaokuNumberSearchModalIsOpen(false);
  };

  const handleClickSubmitBuildingAddRowsButton = (
    cancelLoading: () => void
  ): void => {
    const buildingRows = buildingConvertToPropertySelectionRows();
    appendPropertySelectionRows(buildingRows);
    buildingSetOpenAddRowsModalFlag(false);
    setKaokuNumberSearchModalIsOpen(false);
  };

  const handleClickCloseModalButton = (): void => {
    if (landLoading || buildingLoading) {
      const confirmClose = window.confirm(
        "検索中です。画面を閉じるとキャンセルされます。よろしいですか？"
      );
      if (!confirmClose) {
        return;
      }
    }
    setKaokuNumberSearchModalIsOpen(false);
  };

  // ボタンラベルは variant でのみ分岐
  const actionButtonLabel =
    variant === "map" ? "リストに追加" : "不動産登記簿の一括取得のリストに追加";

  // 確認メッセージ
  const getLandConfirmText = (): string => {
    const landCount = landSelectedKaokuNumberRowIds.length;
    return `家屋番号${formatNumberWithComma(
      landCount
    )}件をリストに追加します。`;
  };

  const getBuildingConfirmText = (): string => {
    const buildingCount = buildingSelectedChibanRowIds.length;
    return `地番${formatNumberWithComma(
      buildingCount
    )}件をリストに追加します。`;
  };

  // SP は PagePaper の余白（24px）だと一覧の幅が足りないため、上の余白だけにする
  const ContentWrapper = isMobile ? MobileContentWrapper : PagePaper;

  return (
    <Box sx={{ p: 2 }}>
      {!hideTitle && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            // SP は横幅が狭いので、タイトルが折り返さないよう左右の余白を詰める
            px: isMobile ? 0 : 2,
            py: 1,
          }}
        >
          <Typography
            variant="h6"
            // SP(320px) でもタイトルが1行に収まるよう少し小さくする
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "1.125rem" : undefined,
            }}
          >
            地番・家屋番号検索
          </Typography>
          {!hideCloseButton && (
            // 共通 Button は SP で幅100%になるため、内容幅で包む
            <Box sx={{ width: "fit-content", flexShrink: 0 }}>
              <Button
                label="閉じる"
                variant={ButtonVariantOption.Outlined}
                onClick={handleClickCloseModalButton}
                size={SizingWrapperStyle.SMALL}
                startIcon={<CloseIcon />}
              />
            </Box>
          )}
        </Box>
      )}
      {/* 実際のタブ切り替え */}
      {/* SP はタブを全幅で等分し、2つ目のタブが画面外に切れないようにする */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: isMobile ? "100%" : "fit-content",
        }}
      >
        {!hideSearchTab && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="検索タブ"
            variant={isMobile ? "fullWidth" : "standard"}
            TabIndicatorProps={{
              sx: {
                height: 3,
                transform: "scaleX(0.8)",
              },
            }}
          >
            <Tab
              label="地番から家屋番号検索"
              sx={isMobile ? { px: 1 } : { mx: 5 }}
            />
            <Tab
              label="家屋番号から地番検索"
              sx={isMobile ? { px: 1 } : { mr: 5 }}
            />
          </Tabs>
        )}
      </Box>
      <ContentWrapper>
        {/* 土地タブ */}
        {activeTab === 0 && (
          <>
            {landData.length === 0 ? (
              <EmptyStateMessage message="地番のデータがありません" />
            ) : (
              <>
                {/* 地番入力 */}
                {!hideChibanList && (
                  <Box>
                    <ViewAreaSearchInput
                      title="地番"
                      buttonLabel="家屋番号検索"
                      dataType="chiban"
                      count={landSelectedChibanRowIds.length}
                      handleRowSelectionChange={handleChibanRowSelectionChange}
                      loading={landLoading}
                      maxRequestCount={landMaxRequestCount}
                      rows={landChibanInputRows}
                      selectedRowIds={landSelectedChibanRowIds}
                      setOpenSearchModalFlag={landSetOpenSearchModalFlag}
                    />
                  </Box>
                )}
                {/* 家屋番号結果 */}
                <Box marginTop={2}>
                  <ViewAreaSearchResult
                    hideListButton={hideListButton}
                    title="家屋番号"
                    buttonLabel={actionButtonLabel}
                    dataType="chiban"
                    disabled={
                      landLoading || landSelectedKaokuNumberRowIds.length === 0
                    }
                    handleRowSelectionChange={
                      handleKaokuNumberRowSelectionChange
                    }
                    loading={landLoading}
                    rows={landKaokuNumberRows}
                    selectedRowIds={landSelectedKaokuNumberRowIds}
                    setOpenAddRowsModalFlag={landSetOpenAddRowsModalFlag}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {/* 建物タブ */}
        {activeTab === 1 && (
          <>
            {buildingData.length === 0 ? (
              <EmptyStateMessage message="家屋番号のデータがありません" />
            ) : (
              <>
                {/* 家屋番号入力（都道府県・所在・家屋番号） */}
                <Box>
                  <ViewAreaSearchInput
                    title="家屋番号"
                    buttonLabel="地番検索"
                    dataType="kaokuNumber"
                    count={buildingSelectedKaokuNumberRowIds.length}
                    handleRowSelectionChange={
                      handleBuildingKaokuNumberRowSelectionChange
                    }
                    loading={buildingLoading}
                    maxRequestCount={buildingMaxRequestCount}
                    rows={buildingKaokuNumberRows}
                    selectedRowIds={buildingSelectedKaokuNumberRowIds}
                    setOpenSearchModalFlag={buildingSetOpenSearchModalFlag}
                    height={300}
                  />
                </Box>
                {/* 結果: 家屋番号・地番一覧 */}
                <Box marginTop={2}>
                  <ViewAreaSearchResult
                    title="地番"
                    buttonLabel={actionButtonLabel}
                    dataType="kaokuNumber"
                    disabled={
                      buildingLoading ||
                      buildingSelectedChibanRowIds.length === 0
                    }
                    handleRowSelectionChange={
                      handleBuildingChibanRowSelectionChange
                    }
                    loading={buildingLoading}
                    rows={buildingChibanRows}
                    selectedRowIds={buildingSelectedChibanRowIds}
                    setOpenAddRowsModalFlag={buildingSetOpenAddRowsModalFlag}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </ContentWrapper>

      {/* LAND: 確認ダイアログ（地番→家屋番号） */}
      <ConfirmDialogSearch
        open={landOpenSearchModalFlag}
        setOpen={landSetOpenSearchModalFlag}
        loading={landLoading}
        onSubmit={handleClickSubmitLandSearchButton}
        description={`地番${formatNumberWithComma(
          landSelectedChibanRowIds.length
        )}件の家屋番号を検索します。`}
        submitLabel="家屋番号検索"
      />

      {/* BUILDING: 検索前確認（家屋番号→地番） */}
      <ConfirmDialogSearch
        open={buildingOpenSearchModalFlag}
        setOpen={buildingSetOpenSearchModalFlag}
        loading={buildingLoading}
        onSubmit={handleClickSubmitBuildingSearchButton}
        description={`家屋番号${formatNumberWithComma(
          buildingSelectedKaokuNumberRowIds.length
        )}件の地番を検索します。`}
        submitLabel="地番検索"
      />

      {/* 土地用確認ダイアログ */}
      <ConfirmDialogKaokuNumber
        dialogContentText={getLandConfirmText()}
        handleClickSubmitAddRowsButton={handleClickSubmitLandAddRowsButton}
        openAddRowsModalFlag={landOpenAddRowsModalFlag}
        setOpenAddRowsModalFlag={landSetOpenAddRowsModalFlag}
      />

      {/* 建物用確認ダイアログ */}
      <ConfirmDialogKaokuNumber
        dialogContentText={getBuildingConfirmText()}
        handleClickSubmitAddRowsButton={handleClickSubmitBuildingAddRowsButton}
        openAddRowsModalFlag={buildingOpenAddRowsModalFlag}
        setOpenAddRowsModalFlag={buildingSetOpenAddRowsModalFlag}
      />
    </Box>
  );
};
